import { downloadCSV } from '../utils/csvExport';
import IntegrationCell from './components/IntegrationCell';
import { ActionsCell, DateCell, StatusCell, TextCell } from './components/IntegrationTableCells';
import {
    PARTNERS_INFORMATION_FEATURE_NAME,
    FORM_TYPES,
    STATUSES,
    ATTRIBUTION_TAB_IDS,
    EVENT_MAPPING_EVENT_TYPES,
    POSTBACK_EVENT_TYPE_OPTIONS,
    POSTBACK_PLATFORM_OPTION,
} from './consts';
import { resolvePostbackUrl, POSTBACK_URL_DOMAIN_PLACEHOLDER } from './platformTemplateDefaults';

export const getStatusForType = (requests, type) => {
    const request = requests.find(r => r.request_type === type);
    if (!request) {
        return { status: STATUSES.NOT_STARTED, request: null };
    }
    return { status: request.status, request };
};

export const formatDate = dateStr => {
    if (!dateStr) return null;
    const date = new Date(dateStr);

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const isFormType = type => FORM_TYPES.includes(type);

export const isPartnerInfoMissing = navigationSections => {
    if (!navigationSections) return false;
    const partnerInfoElement = navigationSections
        .flatMap(section => section.elements || [])
        .find(({ name }) => name === PARTNERS_INFORMATION_FEATURE_NAME);
    return !!partnerInfoElement?.missing_info;
};

export const buildAppLevelPairs = overallData =>
    (overallData.needs_app_level_macro ? overallData.app_level_macros || [] : [])
        .filter(m => m.parameter_key_name)
        .map(m => ({ key: m.parameter_key_name, value: `{{${m.parameter_key_name}}}` }));

export const buildEventMappingPair = (overallData, eventType) => {
    if (!overallData.enable_event_mapping) return [];
    if (!EVENT_MAPPING_EVENT_TYPES.includes(eventType)) return [];
    const key = overallData.event_parameter_key_name?.trim();
    if (!key) return [];
    return [{ key, value: `{{${key}}}` }];
};

export const getPostbackBaseUrl = (overallData, eventType) => {
    if (overallData.different_domains_per_postback) {
        const perEventUrl = overallData[`${eventType}_postback_url`];
        if (perEventUrl?.trim()) return perEventUrl.trim();
    }
    return overallData.postback_url_base || '';
};

export const getUrlSeparator = baseUrl => ((baseUrl || '').includes('?') ? '&' : '?');

export const filterPairs = pairs => (pairs || []).filter(({ key, value }) => key && value);

const buildPostbackUrl = (baseUrl, pairs) => {
    const filled = filterPairs(pairs);
    if (!filled.length) return baseUrl || '';
    const qs = filled.map(({ key, value }) => `${key}=${value}`).join('&');
    return `${baseUrl || ''}${getUrlSeparator(baseUrl)}${qs}`;
};

export const downloadAttributionRequestCsv = (request, orgName) => {
    const formData = request?.form_data || {};
    const generalTab = formData?.[ATTRIBUTION_TAB_IDS.GENERAL] || {};
    const platformTemplate = generalTab.platform_template;
    const overallSetup = formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP] || {};
    const postbackTemplates = formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES] || {};
    const trackingLinkParameters =
        formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]?.tracking_link_parameters || [];

    const appLevelPairs = buildAppLevelPairs(overallSetup);

    const escape = cell => `"${String(cell ?? '').replace(/"/g, '""')}"`;
    const toRow = cells => cells.map(escape).join(',');

    const lines = [toRow(['Platform', 'Type', 'Template'])];

    // Tracking link row — shows ?singular_key=partner_macro for each configured parameter
    const tlPairs = trackingLinkParameters
        .filter(({ singular_key, partner_macro }) => singular_key && partner_macro)
        .map(({ singular_key, partner_macro }) => `${singular_key}=${partner_macro}`)
        .join('&');
    lines.push(toRow(['all', 'tracking link macros', tlPairs ? `?${tlPairs}` : '']));

    // One postback row per configured (event_type, platform) combo, in canonical order
    POSTBACK_EVENT_TYPE_OPTIONS.forEach(eventType => {
        const platformMap = postbackTemplates[eventType];
        if (!platformMap) return;
        POSTBACK_PLATFORM_OPTION.forEach(platform => {
            const macros = platformMap[platform];
            if (!macros?.length) return;
            const pairs = [
                ...macros.map(r => ({ key: r.partner_parameter_key, value: r.singular_macro })),
                ...buildEventMappingPair(overallSetup, eventType),
                ...appLevelPairs,
            ];
            const baseUrl = resolvePostbackUrl(
                platformTemplate,
                eventType,
                overallSetup,
                POSTBACK_URL_DOMAIN_PLACEHOLDER
            );
            const url = buildPostbackUrl(baseUrl || getPostbackBaseUrl(overallSetup, eventType), pairs);
            lines.push(toRow([platform, eventType, url]));
        });
    });

    const orgSlug = (orgName || String(request.id))
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_-]/g, '');
    downloadCSV(lines.join('\n'), `integration_${request.request_type}_${orgSlug}_templates.csv`);
};

export const getIntegrationTableMetadata = ({ translatePrefix, adminMode = false }) => {
    const columns = [
        {
            name: 'integration',
            displayName: `${translatePrefix}.TABLE_HEADERS.INTEGRATION`,
            cellComponent: IntegrationCell,
        },
        {
            name: 'status',
            displayName: `${translatePrefix}.TABLE_HEADERS.STATUS`,
            cellComponent: StatusCell,
            headerWidth: '140px',
        },
        {
            name: 'lastEdited',
            displayName: `${translatePrefix}.TABLE_HEADERS.LAST_EDITED`,
            cellComponent: DateCell,
            headerWidth: '140px',
        },
    ];

    if (adminMode) {
        columns.push(
            {
                name: 'submittedAt',
                displayName: `${translatePrefix}.TABLE_HEADERS.SUBMITTED_AT`,
                cellComponent: DateCell,
                headerWidth: '140px',
            },
            {
                name: 'reviewer',
                displayName: `${translatePrefix}.TABLE_HEADERS.REVIEWER`,
                cellComponent: TextCell,
                headerWidth: '150px',
            },
            {
                name: 'reviewerComments',
                displayName: `${translatePrefix}.TABLE_HEADERS.REVIEWER_COMMENTS`,
                cellComponent: TextCell,
                headerWidth: '200px',
            }
        );
    }

    columns.push({
        name: 'actions',
        displayName: `${translatePrefix}.TABLE_HEADERS.ACTIONS`,
        cellComponent: ActionsCell,
        headerAlign: 'right',
        headerWidth: '220px',
    });

    return columns;
};
